import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { GoldButton } from "@/components/primitives";
import { nav, site } from "@/config/site";
import { cn } from "@/lib/utils";

function Monogram() {
  return (
    <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
      <span className="flex h-9 w-9 items-center justify-center border border-fs-gold/50 font-serif text-base tracking-tight text-fs-gold">
        FS
      </span>
      <span className="font-serif text-xl tracking-tight text-fs-ink">
        Fortuna <span className="text-fs-gold">Strive</span>
      </span>
    </Link>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500",
        scrolled
          ? "border-b border-fs-border bg-fs-bg/80 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      )}
    >
      <div className="fs-shell flex h-[70px] items-center justify-between">
        <Monogram />

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                cn(
                  "relative text-sm font-medium tracking-wide transition-colors duration-300 hover:text-fs-ink",
                  isActive ? "text-fs-ink" : "text-fs-muted"
                )
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  <span
                    className={cn(
                      "absolute -bottom-1.5 left-0 h-px bg-fs-gold transition-all duration-300",
                      isActive ? "w-full" : "w-0"
                    )}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <GoldButton to="/contact" testId="nav-cta" className="px-6 py-2.5 text-sm">
            Let's Talk
          </GoldButton>
        </div>

        {/* Mobile */}
        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                aria-label="Open menu"
                data-testid="mobile-menu-trigger"
                className="flex h-10 w-10 items-center justify-center border border-fs-border text-fs-ink"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[85vw] max-w-sm border-l border-fs-border bg-fs-bg2 text-fs-ink"
            >
              <div className="mt-10 flex flex-col gap-2" data-testid="mobile-nav">
                {nav.map((item) => (
                  <SheetClose asChild key={item.to}>
                    <NavLink
                      to={item.to}
                      data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                      className={({ isActive }) =>
                        cn(
                          "border-b border-fs-border py-4 font-serif text-2xl tracking-tight transition-colors",
                          isActive ? "text-fs-gold" : "text-fs-ink hover:text-fs-gold"
                        )
                      }
                    >
                      {item.label}
                    </NavLink>
                  </SheetClose>
                ))}
                <SheetClose asChild>
                  <GoldButton to="/contact" testId="mobile-nav-cta" className="mt-6 w-full">
                    Let's Talk
                  </GoldButton>
                </SheetClose>
                <a
                  href={`mailto:${site.contactEmail}`}
                  className="mt-6 text-sm text-fs-muted"
                >
                  {site.contactEmail}
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
